<script setup lang="ts">
  import { VButton, VPopover } from "@krainovsd/vue-ui";
  import Sheet from "@/components/atoms/Sheet.vue";
  import ChevronDown from "@/components/icons/ChevronDown.vue";

  type Mode = "popover" | "sheet";

  type Props = {
    value?: string | number | null;
    placeholder: string;
    mode?: Mode;
    title?: string;
    subtitle?: string;
  };

  const props = withDefaults(defineProps<Props>(), {
    value: null,
    mode: "popover",
  });

  // Optional controlled open state for sheet mode (v-model:open). Falls back to
  // local state when the parent doesn't bind it.
  const sheetOpen = defineModel<boolean>("open", { default: false });
</script>

<template>
  <VPopover
    v-if="props.mode === 'popover'"
    v-model="sheetOpen"
    :class-name-positioner-content="$style.positioner"
  >
    <VButton shape="round" size="large" icon-position="right" :class="$style.button">
      <span :class="{ [$style.placeholder]: props.value == null }">
        {{ props.value ?? props.placeholder }}
      </span>
      <template #icon>
        <ChevronDown />
      </template>
    </VButton>
    <template #content>
      <slot />
    </template>
  </VPopover>

  <template v-else>
    <VButton
      shape="round"
      size="large"
      icon-position="right"
      :class="$style.button"
      @click="sheetOpen = true"
    >
      <span :class="{ [$style.placeholder]: props.value == null }">
        {{ props.value ?? props.placeholder }}
      </span>
      <template #icon>
        <ChevronDown />
      </template>
    </VButton>
    <Sheet v-model:open="sheetOpen" :title="props.title" :subtitle="props.subtitle">
      <slot />
    </Sheet>
  </template>
</template>

<style lang="scss" module>
  .placeholder {
    color: var(--muted-foreground);
  }

  .positioner {
    min-width: 200px;
    padding: 4px;

    background-color: var(--popover);
  }

  .button {
    $height: 44px;
    height: $height !important;
    min-height: $height !important;
    padding: 0.375rem 0.75rem;

    font-size: 0.875rem;
    line-height: 1.25rem;

    gap: 0.375rem;

    color: var(--foreground);
    background-color: var(--card);
  }
</style>
